import { Translations } from '../i18n/translations';
import { SystemState } from '../components/SystemStatus';

export interface SystemStateInputs {
  aiChecking: boolean;
  aiAvailable: boolean;
  loading: boolean;
  feedsErrored: boolean;
  failedFeedCount: number;
  // Free-form analysis status string from feedStatus.analysis. We pattern-
  // match keywords (retry / pending / analyz) instead of comparing to
  // exact translated strings so it stays robust under i18n.
  analysisStatus: string | undefined;
  hasFeeds: boolean;
}

// Pure helper: maps the wave's transient state + the engine's capability
// into a single (kind, note) pair. Kept apart from the React component so
// the derivation can be unit-tested without rendering.
export const deriveSystemState = (
  inputs: SystemStateInputs,
  t: Translations,
): SystemState => {
  const {
    aiChecking,
    aiAvailable,
    loading,
    feedsErrored,
    failedFeedCount,
    analysisStatus,
    hasFeeds,
  } = inputs;

  // No feeds at all — the engine isn't really "running", but it's not in
  // a failure state either. Show as OK so a new user doesn't think
  // anything is broken before they've added a source.
  if (!hasFeeds) return { kind: 'ok', note: t.systemStateIdle };

  if (feedsErrored) {
    return { kind: 'down', note: t.systemStateAllOffline };
  }

  if (aiChecking) {
    return { kind: 'degraded', note: t.systemStateStarting };
  }

  if (loading) {
    return { kind: 'degraded', note: t.systemStateRefreshing };
  }

  if (failedFeedCount > 0) {
    const note = failedFeedCount === 1
      ? t.systemStateOneFeedOffline
      : t.systemStateFeedsOffline.replace('{count}', String(failedFeedCount));
    return { kind: 'degraded', note };
  }

  const rawAnalysis = analysisStatus || '';
  const analysisLower = rawAnalysis.toLowerCase();
  // A status starting with "✓" is a completed-state marker — even if its
  // body mentions "retry" or "complete via retry", we should treat it as
  // done, not as in-progress. Without this guard the pill would falsely
  // flip to amber on a successful background-retry result.
  const isComplete = rawAnalysis.startsWith('✓');
  if (!isComplete && (analysisLower.includes('retry') || analysisLower.includes('pending'))) {
    return { kind: 'degraded', note: t.systemStateRetrying };
  }
  if (!isComplete && (analysisLower.startsWith('analy') || analysisLower.includes('analiz'))) {
    return { kind: 'degraded', note: t.systemStateAnalyzing };
  }

  if (!aiAvailable) {
    return {
      kind: 'degraded',
      note: t.systemStateLite,
      detail: t.systemStateLiteDetail,
    };
  }

  return { kind: 'ok', note: t.systemStateOk };
};
